import { useEffect, useState } from "react";
import { conflictSlotDates, encodeConflictDates, guestComparison, isHouseChoice, slotLabel, type DateConflicts, type VenueConflict } from "../../shared/venue-conflicts";
import type { DaySlot, VenueChoice } from "../../shared/tasting";
import { api } from "../api";

const HOUSE_LABEL: Record<string, string> = { casa: "CASA", ktema: "ΚΤΗΜΑ", regina: "REGINA" };

function shortDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * Live clashes for the chosen house on the form's interest dates, at the slot chosen for each date
 * (all slots when none is chosen). It refetches shortly after a date, slot or venue changes, and
 * leaves out the current event and card.
 */
export function useVenueConflicts(venue: VenueChoice | null, rows: { date?: string | null; slot?: DaySlot | null }[], excludeEvent: string | null, excludeVisit: string | null): Map<string, VenueConflict[]> {
  const [result, setResult] = useState<Map<string, VenueConflict[]>>(new Map());
  const wanted = conflictSlotDates(rows);
  const encoded = encodeConflictDates(wanted);
  const key = `${venue ?? ""}|${encoded}|${excludeEvent ?? ""}|${excludeVisit ?? ""}`;
  useEffect(() => {
    if (!isHouseChoice(venue) || !wanted.length) {
      setResult(new Map());
      return;
    }
    let cancel = false;
    const params = new URLSearchParams({ venue, dates: encoded });
    if (excludeEvent) params.set("exclude_event", excludeEvent);
    if (excludeVisit) params.set("exclude_visit", excludeVisit);
    const timer = window.setTimeout(() => {
      void api<{ dates: DateConflicts[] }>(`/api/tastings/conflicts?${params.toString()}`)
        .then((data) => {
          if (!cancel) setResult(new Map(data.dates.map((entry) => [entry.date, entry.conflicts])));
        })
        .catch(() => {
          if (!cancel) setResult(new Map());
        });
    }, 250);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [key]);
  return result;
}

const NO_SLOT = "χωρίς ώρα";

/** A short line under a date row in «Ημερομηνίες ενδιαφέροντος», e.g. «⚠ REGINA μεσημέρι: 1 ενδιαφέρον». */
export function ConflictHint({ conflicts, venue, slot }: { conflicts: VenueConflict[] | undefined; venue: VenueChoice | null; slot?: DaySlot | null }) {
  if (!conflicts?.length || !venue) return null;
  const closed = conflicts.filter((conflict) => conflict.kind === "closed").length;
  const interest = conflicts.length - closed;
  const unknown = slot ? conflicts.filter((conflict) => !conflict.slot).length : 0;
  const parts = [closed ? `${closed} κλεισμένο` : null, interest ? `${interest} ενδιαφέρον` : null].filter(Boolean).join(" · ");
  const where = [HOUSE_LABEL[venue] ?? venue, slotLabel(slot)].filter(Boolean).join(" ");
  return <p className="conflict-hint" role="status">⚠ {where}: {parts}{unknown ? ` (${unknown} ${NO_SLOT})` : ""} — δες «Χώρος & Καλεσμένοι»</p>;
}

/** The full warning in «Χώρος & Καλεσμένοι»: every clash per date (and chosen slot), next to this client's guests. */
export function VenueConflictWarning({ conflicts, venue, guests, slots }: { conflicts: Map<string, VenueConflict[]>; venue: VenueChoice | null; guests: number | null; slots?: Map<string, DaySlot | null> }) {
  if (!venue || !conflicts.size) return null;
  const dates = [...conflicts.keys()].sort();
  return (
    <div className="conflict-box" role="alert">
      <strong>⚠ Υπάρχει ήδη ζήτηση στο {HOUSE_LABEL[venue] ?? venue} για τις ημερομηνίες του πελάτη</strong>
      {dates.map((date) => {
        const list = conflicts.get(date) ?? [];
        const chosen = slotLabel(slots?.get(date));
        return (
          <div className="conflict-date" key={date}>
            <div className="conflict-head">
              <span>{shortDate(date)}{chosen ? ` · ${chosen}` : ""}</span>
              <span className="conflict-compare">{guestComparison(guests, list)}</span>
            </div>
            <ul>
              {list.map((conflict) => (
                <li key={conflict.event_id}>
                  <span className={`conflict-tag ${conflict.kind}`}>{conflict.status_label}</span>
                  <span className="conflict-name">{conflict.name}</span>
                  <span>{conflict.type_label}</span>
                  <span className={conflict.slot ? "conflict-slot" : "conflict-slot unknown"}>{slotLabel(conflict.slot) ?? NO_SLOT}</span>
                  <span className="conflict-guests">{conflict.guests != null ? `${conflict.guests} άτομα` : "— άτομα"}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <p className="conflict-own">Αυτός ο πελάτης: {guests != null ? `${guests} άτομα` : "δεν έχει δηλωθεί αριθμός"}</p>
    </div>
  );
}
